import type { ExchangePerformanceTimeframeEnumType, FloatBucketEnumType, FloatBucketTimeframeEnumType, TimeframeEnumType } from '@forgd/shared/enums'
import { formatValue } from '@forgd/formatter'

/**
 * Format a decimal fraction as a percent with a sign prefix.
 * Positive values get a leading `+`; zero and negatives use the default sign.
 */
export function formatSignedPercent(value: number, decimals: 1 | 2): string {
  const formatted = formatValue(value, `percent:${decimals}`)
  return value > 0 ? `+${formatted}` : formatted
}

export function median(values: number[]): number | null {
  if (!values.length) {
    return null
  }
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2
}

const MM_LOGO_BASE = '/images/market-maker-logos/'
const MM_LOGO_FALLBACK = `${MM_LOGO_BASE}other.png`

export function resolveMmLogo(path?: string | null): string {
  if (!path) {
    return MM_LOGO_FALLBACK
  }
  if (path.startsWith('http') || path.startsWith('/')) {
    return path
  }
  return MM_LOGO_BASE + path
}

export interface Period {
  startDate: string
  endDate: string
}

export const chartTimeframeLabels: Record<TimeframeEnumType, string> = {
  '24h': '24H Performance',
  '7d': '7 Day Performance',
  '30d': '30 Day Performance',
  '60d': '60 Day Performance',
  '90d': '90 Day Performance',
  '180d': '180 Day Performance',
  '365d': '365 Day Performance',
  'all': 'All-Time Performance',
  'latest': 'All-Time Performance',
}

export const tableTimeframeLabels: Record<TimeframeEnumType, string> = {
  '24h': '24 Hours',
  '7d': '7 Days',
  '30d': '30 Days',
  '60d': '60 Days',
  '90d': '90 Days',
  '180d': '180 Days',
  '365d': '365 Days',
  'all': 'All-Time',
  'latest': 'All-Time',
}

export const floatBucketTimeframeLabels: Record<FloatBucketTimeframeEnumType, string> = {
  '1h': '1 Hour',
  '1d': '1 Day',
  '7d': '7 Days',
  '1m': '1 Month',
  '3m': '3 Months',
  '6m': '6 Months',
  '12m': '12 Months',
}

export const floatBucketLabels: Record<FloatBucketEnumType, string> = {
  'gt_50': '>50%',
  '25_50': '25-50%',
  '10_25': '10-25%',
  'lt_10': '<10%',
}

export const exchangePerformanceTimeframeLabels: Record<ExchangePerformanceTimeframeEnumType, string> = {
  'tge': 'TGE',
  '1d': '1 Day',
  '1w': '1 Week',
  '1m': '1 Month',
  '2m': '2 Months',
  '3m': '3 Months',
  'all_time': '1 Year',
}

interface DomainRangeOptions {
  defaultMin?: number
  defaultMax?: number
  clampMin?: number
  clampMax?: number
}

export function getDomainRange<T>(
  data: T[] | undefined,
  accessor: (item: T) => number,
  options: DomainRangeOptions = {},
): [number, number] {
  const { defaultMin = 0, defaultMax = 100, clampMin, clampMax } = options
  const list = data?.map(accessor) ?? []

  let min = list.length > 0 ? Math.min(...list) : defaultMin
  let max = list.length > 0 ? Math.max(...list) : defaultMax

  if (clampMin !== undefined) {
    min = Math.min(min, clampMin)
  }
  if (clampMax !== undefined) {
    max = Math.max(max, clampMax)
  }

  return [min, max]
}

interface ChartHeightOptions {
  legendHeight: number
  gap?: number
  padding?: number
  cardHeight?: number
  titleHeight?: number
}

export function calculateChartHeight(options: ChartHeightOptions) {
  const {
    legendHeight,
    gap = 20,
    padding = 32,
    cardHeight = 400,
    titleHeight = 26,
  } = options

  return cardHeight - gap - padding - titleHeight - legendHeight
}

/**
 * Escapes special XML/SVG characters in text for safe rendering in SVG elements
 * D3/Unovis should handle this automatically, but this can be used as a fallback
 */
export function escapeSvgText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export {
  calculateAverageGradientColor,
  calculateGradientColor,
  convertGradientToRgb,
  gradientCold,
  type GradientColorRgb,
  gradientHot,
  type GradientStop,
  hexToRgb,
  isLightColor,
  normalizeValue,
  parseColorToRgb,
} from '#core/utils/gradient'

interface MetricBreakdown {
  label: string
  value: number | null
}

interface BottomStat {
  label: string
  value: string
  rank?: number | null
}
export interface MmIntelligenceItem {
  rank: number
  name: string
  imageUrl: string | null
  activeEngagementsCount: number | null
  metrics: MetricBreakdown[]
  bottomStats: BottomStat[]
  accentColor: string
}
